import { checkTimeRange } from '../../../helper/Occasions';
import { FilterFunction } from './filterConfig';

/**
 * Filter: Hide occasions from hidden facilities
 */
export const filterHiddenFacilities: FilterFunction = (occasion, activity, state) => {
	if (!activity) return false;
	if (state.searchTerm.length > 0) return true;
	return !state.hiddenFacilities.includes(activity.facilityId ?? 0);
};

/**
 * Filter: After work hours (16:00 - 19:00)
 */
export const filterAfterWork: FilterFunction = (occasion, activity, state) => {
	if (!state.showAfterWork) return true;
	return checkTimeRange(occasion);
};

/**
 * Filter: Ladies-only sessions
 */
export const filterLadiesOnly: FilterFunction = (occasion, activity, state) => {
	const activityName = activity?.name?.toLocaleLowerCase();
	if (!activityName) return !state.ladiesOnly;

	const disallowedPhrases = ['dam/herr', 'dam och herr'];
	const isDisallowed = disallowedPhrases.some((phrase) => activityName.includes(phrase));
	const ladiesPattern = /\bdam(er)?\b/;
	const gittanPattern = /gittan/;
	const girlPattern = /girl/;
	const isLadiesSession =
		ladiesPattern.test(activityName) || gittanPattern.test(activityName) || (girlPattern.test(activityName) && !isDisallowed);

	return state.ladiesOnly ? isLadiesSession : !isLadiesSession;
};

/**
 * Filter: Senior sessions
 */
export const filterSeniorOnly: FilterFunction = (occasion, activity, state) => {
	const activityName = activity?.name?.toLocaleLowerCase();
	if (!activityName) return !state.seniorOnly;

	const seniorPattern = /senior/;
	const isSeniorSession = seniorPattern.test(activityName);

	return state.seniorOnly ? isSeniorSession : !isSeniorSession;
};

/**
 * Filter: Mixed sessions
 */
export const filterMixedOnly: FilterFunction = (occasion, activity, state) => {
	const activityName = activity?.name?.toLocaleLowerCase();
	if (!activityName) return !state.mixedOnly;

	const isMixedSession = activityName.includes('mixed');

	return state.mixedOnly ? isMixedSession : !isMixedSession;
};

/**
 * Filter: Private lessons
 */
export const filterPrivateLesson: FilterFunction = (occasion, activity, state) => {
	const activityName = activity?.name?.toLocaleLowerCase();
	if (!activityName) return !state.privateLesson;

	const privateLessonPattern = /privatlektion/;
	const trainingPattern = /träning/;
	const coachPattern = /tränare/;
	const matchesFilter = privateLessonPattern.test(activityName) || trainingPattern.test(activityName) || coachPattern.test(activityName);

	return state.privateLesson ? matchesFilter : !matchesFilter;
};

const ACTIVITY_BANLIST = [
	'Queens of Padel - Delang Edition',
	'Mexicano nivå 2-4 söndagar 11-12.30',
];

export const filterBanlist: FilterFunction = (occasion, activity) => {
	if (!activity?.name) return true;
	return !ACTIVITY_BANLIST.includes(activity.name);
};

/**
 * Filter: Machine sessions
 */
export const filterShowMachine: FilterFunction = (occasion, activity, state) => {
	if (state.showMachine) return true;
	return !activity?.name?.toLocaleLowerCase().includes('bollmaskin');
};

/**
 * Filter: Hide sessions that end 21:00 or later
 */
export const filterHideLateEvening: FilterFunction = (occasion, activity, state) => {
	if (!state.hideLateEvening) return true;
	const endTime = new Date(occasion.endTime);
	const cutoff = new Date(endTime);
	cutoff.setHours(21, 0, 0, 0);
	return endTime < cutoff;
};

/**
 * Filter: Only empty slots or search term matches
 */
export const filterOnlyEmptyOrSearch: FilterFunction = (occasion, activity, state) => {
	if (state.showOnlyEmpty) {
		return occasion.occasionParticipants.length === 0;
	}

	// If there's a search term, filter by participant names
	return occasion.occasionParticipants.some((participant) => {
		if (state.searchTerm.length === 0) {
			return true;
		}
		const term = state.searchTerm.toLocaleLowerCase();
		if (String(participant.userId).includes(term)) {
			return true;
		}
		if (state.searchTerm.split(' ').length === 1) {
			return (
				participant.firstName?.toLocaleLowerCase().includes(term) ||
				participant.lastName?.toLocaleLowerCase().includes(term)
			);
		} else if (state.searchTerm.split(' ').length === 2) {
			return `${participant.firstName?.toLocaleLowerCase()} ${participant.lastName?.toLocaleLowerCase()}`.includes(term);
		} else {
			return false;
		}
	});
};

/**
 * Filter: Minimum level requirement
 * Note: This filter is bypassed when certain other filters are active
 */
export const filterMinLevel: FilterFunction = (occasion, activity, state) => {
	if (state.minLevel === null) return true;
	if (!activity) return false;

	return activity.level?.min === null || activity.level.min >= state.minLevel;
};

/**
 * All filter functions in order of application
 */
export const FILTER_FUNCTIONS: FilterFunction[] = [
	filterBanlist,
	filterHiddenFacilities,
	filterAfterWork,
	filterLadiesOnly,
	filterSeniorOnly,
	filterMixedOnly,
	filterPrivateLesson,
	filterShowMachine,
	filterHideLateEvening,
	filterOnlyEmptyOrSearch,
	filterMinLevel,
];
